import { logger } from '@/utils/logger';
import { askConfirm } from '@/components/shared/ConfirmationDialog';
import { useBlocker } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useRef } from 'react';

/**
 * ============================================================================
 * Module-level LIFO stack for coordinating multiple blockers
 *
 * The implementation adds a module-level LIFO stack to useBackBlocker that
 * coordinates which blocker's shouldBlockFn returns true. Each hook instance
 * registers on mount and unregisters on unmount. When TanStack Router
 * evaluates blockers, only the topmost enabled entry in the stack claims the
 * event — all others return false and get skipped. This restores the old
 * useBackKey behavior where nested dialogs close one-at-a-time from top to
 * bottom, without requiring any changes to consumers.
 * ============================================================================
 */

interface BackBlockerEntry {
  id: string;
  debugLabel?: string;
  enabled: boolean;
}

const stack: BackBlockerEntry[] = [];

/** Deduplicates by `id` — if React re-mounts without cleanup, the same
 *  logical blocker won't appear twice on the stack. */
function register(id: string, debugLabel?: string): BackBlockerEntry {
  const existing = stack.find((e) => e.id === id);
  if (existing) return existing;
  const entry: BackBlockerEntry = { id, debugLabel, enabled: false };
  stack.push(entry);
  return entry;
}

function unregister(entry: BackBlockerEntry): void {
  const idx = stack.indexOf(entry);
  if (idx !== -1) {
    stack.splice(idx, 1);
  } else if (import.meta.env.DEV) {
    logger.warn(
      `[useBackBlocker] unregister called for entry not in stack (id=${entry.id}${entry.debugLabel ? `, label=${entry.debugLabel}` : ''})`
    );
  }
}

function isTopmost(entry: BackBlockerEntry): boolean {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].enabled) {
      return stack[i] === entry;
    }
  }
  return false;
}

// ============================================================================
// Hook
// ============================================================================

type UseBackBlockerOpts = {
  enabled: boolean;
  onBack: () => void;
  askConfirm?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  id?: string;
};

export function useBackBlocker(opts: UseBackBlockerOpts) {
  // Stable identity across re-mounts — prevents double-register on the stack
  const id = useId();
  const entryRef = useRef<BackBlockerEntry | null>(null);
  if (entryRef.current === null) {
    entryRef.current = register(id, opts.id);
  }
  entryRef.current.enabled = opts.enabled;

  useEffect(() => {
    return () => {
      if (entryRef.current) {
        unregister(entryRef.current);
        entryRef.current = null;
      }
    };
  }, []);

  // Stable shouldBlockFn via refs to prevent TanStack useEffect churn
  const enabledRef = useRef(opts.enabled);
  enabledRef.current = opts.enabled;

  const shouldBlockFn = useCallback(() => {
    return enabledRef.current && isTopmost(entryRef.current!);
  }, []);

  const blocker = useBlocker({
    shouldBlockFn,
    withResolver: true,
    disabled: !opts.enabled,
  });

  const optionsRef = useRef(opts);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    optionsRef.current = opts;
    blockerRef.current = blocker;
  }, [opts.onBack, blocker, opts]);

  useEffect(() => {
    if (blockerRef.current.status !== 'blocked') return;

    if (optionsRef.current.askConfirm) {
      askConfirm({
        title: optionsRef.current.confirmTitle ?? 'Are you sure?',
        description:
          optionsRef.current.confirmDescription ??
          'You will lose your changes.',
        onConfirm: () => {
          optionsRef.current.onBack();
          blockerRef.current?.proceed?.();
        },
        onCancel: () => blockerRef.current?.reset?.(),
      });
    } else {
      optionsRef.current.onBack();
      blockerRef.current?.reset?.();
    }
  }, [blocker.status]);
}

export default useBackBlocker;
